/**
 * In-memory follow-up question provider for the v7 modal (previews / manual QA).
 *
 * The modal depends on a *provider* rather than calling `ChatService` directly
 * (dependency inversion). In production it uses its own live provider; for
 * offline work pass `createStubProvider(questions)` and the modal walks an
 * in-memory question array via `next` pointers, exactly like the BE — no test
 * framework required. See Playground.vue.
 *
 * A provider implements three methods, each resolving to a response whose
 * shape mirrors `ChatService` (`ResponseWrapper`, i.e. `.data` is the body):
 *   - getFollowUpQuestion(messageId, step)        -> { data: Question[] }
 *   - answerFollowUpQuestion(messageId, step, {}) -> { data: { next } }
 *   - getPreviousFollowUpQuestion(messageId, step)-> { data: Question[] }
 */

const sameId = (a, b) => String(a) === String(b);

/**
 * In-memory provider for previews / manual QA.
 * @param {Array} questions - the v7 question array (see src/playground/follow-up/v7/questions.fixture.js)
 * @param {Object} [opts]
 * @param {Object} [opts.doctor] - doctor record attached to `otherrecords:["doctor"]` screens
 */
export function createStubProvider(questions, { doctor } = {}) {
  const fallbackDoctor = doctor || {
    firstname: 'Jane',
    lastname: 'Doe',
    profileUrl: '',
  };
  const findById = id => questions.find(q => sameId(q.id, id));
  const decorate = question => {
    if (!question) return question;
    const clone = JSON.parse(JSON.stringify(question));
    if ((clone.otherrecords || []).includes('doctor')) {
      clone.doctor = fallbackDoctor;
    }
    return clone;
  };

  const history = [];

  return {
    async getFollowUpQuestion(messageId, step) {
      const question = findById(step);
      if (question) history.push(question.id);
      return { data: question ? [decorate(question)] : [] };
    },

    async answerFollowUpQuestion(messageId, step, payload) {
      const answers = (payload && payload.answers) || [];
      const last = answers[answers.length - 1] || {};
      const question = findById(last.questionid);
      let next = question && question.next != null ? question.next : -1;
      if (question && Array.isArray(question.answerlist)) {
        const entry = question.answerlist.find(a => sameId(a.id, last.answer));
        if (entry && entry.next != null) next = entry.next;
      }
      return { data: { next } };
    },

    async getPreviousFollowUpQuestion(_messageId, _step) {
      history.pop(); // drop current
      const prevId = history[history.length - 1];
      const question = findById(prevId);
      return { data: question ? [decorate(question)] : [] };
    },
  };
}
